/**
 * NotificationInfo 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'

/**
 * NotificationInfo
 */
export interface NotificationInfo {
  /** 创建时间（UTC） */
  created_at?: string
  /** 更新时间（UTC） */
  updated_at?: string
  /** 主键（UUID4），全局唯一，不可重复，36位 */
  id?: string
  /** 删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped() */
  deleted_at?: string
  /** 删除原因 */
  deleted_reason?: string
  /** 投递类型：direct(个人通知), broadcast(广播通知) */
  delivery_type?: string
  /** 接收者用户ID（delivery_type=direct 时必填） */
  recipient_user_id?: string
  /** 受众标识（delivery_type=broadcast 时必填，如 all, followers:uuid） */
  audience_key?: string
  /** 发送者用户ID（系统通知时为空或官方用户ID） */
  sender_id?: string
  /** 通知类型：like, comment, follow, system 等 */
  type?: string
  /** 通知标题 */
  title?: string
  /** 通知正文 */
  body?: string
  /** 点击跳转URL（支持 Deep Link） */
  action_url?: string
  /** 通知图片URL */
  image_url?: string
  /** 关联对象类型：post, comment, user 等 */
  target_type?: string
  /** 关联对象ID */
  target_id?: string
  /** 额外业务数据（JSON） */
  extra_data?: string
  /** 是否已读（仅个人通知使用） */
  is_read?: boolean
  /** 阅读时间（仅个人通知使用） */
  read_at?: string
  /** 是否已推送到 OneSignal */
  pushed?: boolean
  /** 推送时间 */
  pushed_at?: string
}

/**
 * NotificationInfo Schema
 */
export const NotificationInfoSchema: SchemaDefinition = {
  properties: {
    created_at: {
      description: '创建时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '创建时间',
    },
    updated_at: {
      description: '更新时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '更新时间',
    },
    id: {
      description: '主键（UUID4），全局唯一，不可重复，36位',
      format: 'string',
      type: 'string',
      label: '主键',
    },
    deleted_at: {
      description:
        '删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped()',
      format: '*time.Time',
      type: 'string',
      label: '删除时间',
    },
    deleted_reason: {
      description: '删除原因',
      format: 'string',
      type: 'string',
      label: '删除原因',
    },
    delivery_type: {
      description: '投递类型：direct(个人通知), broadcast(广播通知)',
      format: 'cons.NotificationDeliveryType',
      type: 'string',
      label: '投递类型：direct',
    },
    recipient_user_id: {
      description: '接收者用户ID（delivery_type=direct 时必填）',
      format: 'string',
      type: 'string',
      label: '接收者用户ID',
    },
    audience_key: {
      description: '受众标识（delivery_type=broadcast 时必填，如 all, followers:uuid）',
      format: 'string',
      type: 'string',
      label: '受众标识',
    },
    sender_id: {
      description: '发送者用户ID（系统通知时为空或官方用户ID）',
      format: 'string',
      type: 'string',
      label: '发送者用户ID',
    },
    type: {
      description: '通知类型：like, comment, follow, system 等',
      format: 'cons.NotificationType',
      type: 'string',
      label: '通知类型：like',
    },
    title: {
      description: '通知标题',
      format: 'string',
      type: 'string',
      label: '通知标题',
    },
    body: {
      description: '通知正文',
      format: 'string',
      type: 'string',
      label: '通知正文',
    },
    action_url: {
      description: '点击跳转URL（支持 Deep Link）',
      format: 'string',
      type: 'string',
      label: '点击跳转URL',
    },
    image_url: {
      description: '通知图片URL',
      format: 'string',
      type: 'string',
      label: '通知图片URL',
    },
    target_type: {
      description: '关联对象类型：post, comment, user 等',
      format: 'cons.NotificationTargetType',
      type: 'string',
      label: '关联对象类型：post',
    },
    target_id: {
      description: '关联对象ID',
      format: 'string',
      type: 'string',
      label: '关联对象ID',
    },
    extra_data: {
      description: '额外业务数据（JSON）',
      format: 'string',
      type: 'string',
      label: '额外业务数据',
    },
    is_read: {
      description: '是否已读（仅个人通知使用）',
      format: 'bool',
      type: 'boolean',
      label: '是否已读',
    },
    read_at: {
      description: '阅读时间（仅个人通知使用）',
      format: '*time.Time',
      type: 'string',
      label: '阅读时间',
    },
    pushed: {
      description: '是否已推送到 OneSignal',
      format: 'bool',
      type: 'boolean',
      label: '是否已推送到',
    },
    pushed_at: {
      description: '推送时间',
      format: '*time.Time',
      type: 'string',
      label: '推送时间',
    },
  },
  type: 'object',
}
